import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { getCurrentUser } from 'redux/selectors/users';

// Translations
import t from 'react-translate';

// Types
import { SessionTypes, LiveEventSession as LiveEventSessionType, ZoomSession, MSTeamsSession } from 'redux/schemas/models/live-event';
import { FroalaViewMode, UploadType } from 'froala/helpers/nv-froala-constants';

// Styles
import { css } from '@emotion/react';
import { gray4, gray7 } from 'styles/global_defaults/colors';
import { iconSmallSize } from 'styles/global_defaults/icons';
import { getSanitizedStyles } from 'shared/utils';

// Components
import NvFroala from 'froala/components/nv-froala';
import NvDatePicker, { DatePickerType, getMomentDate } from 'shared/components/inputs/nv-datepicker';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { FormNvClickToEdit, InputType } from 'components/nv-click-to-edit';
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import NvTimezoneSelector from 'shared/components/nv-timezone-selector';
import ClickableContainer from 'components/clickable-container';
import { config } from '@config/pendo.config.json';
import ZoomHostValidator from './zoom-host-validator';
import MSTeamsCoOrganizerValidator from './ms-teams-co-organizer-validator';

import { LiveSessionFormContext } from './live-session-form';

const styles = css`
  background-color: ${gray7};

  .badge-index {
    width: ${iconSmallSize}px;
    height: ${iconSmallSize}px;
    border-radius: 10px;
    color: white;
  }

  .fr-wrapper {
    .fr-element {
      max-height: 55px;
      min-height: unset !important;
      outline: none;
    }
  }

  .timing {
    gap: 10px;
    .timing-start-time {
      flex-grow: 1;
      .react-datepicker-wrapper {
        width: 100%;
      }
    }
    .timing-timezone {
      width: 150px;
    }
    .timing-duration {
      width: 130px;
    }
  }
  .timing-timezone, .timing-duration {
    width: calc(100% - 140px);
    &.disabled {
      .bs4-dropdown {
        pointer-events: none;
        background-color: transparent;
        border-color: ${gray4};
        color: inherit;
      }
    }
    .timezone-selector {
      width: 100%;
      .icon-dropdown-arrow {
        transform: scale(0.75);
      }
    }
  }
  .bs4-dropdown-menu {
    max-height: 250px;
    overflow-y: scroll;
  }
  .live-event-topic {
    word-break: break-all;
  }
`;

type LiveEventSessionProps = {
  session: LiveEventSessionType
  showIndex: boolean
  canDelete: boolean
  delete: () => void
  onHostValidation?: (
    isValid: boolean,
    validHosts: { zoomHost?: string, alternativeHosts?: string, coorganizers?: string },
    allValid?: boolean
  ) => void
};

const labelClass = 'font-weight-bold text-small gray-3 m-0';

const LiveEventSession = (props: LiveEventSessionProps) => {
  const {
    session,
    showIndex,
    canDelete,
    delete: deleteSession,
    onHostValidation,
  } = props;

  const {
    sessionType,
    isContentManagementCollection,
    isLinked,
    durations,
    getDurationText,
    showZoomHostValidator,
  } = useContext(LiveSessionFormContext);

  const [disabled, setDisabled] = useState(false);

  const [disabledAlternativeHosts, setDisabledAlternativeHosts] = useState(false);
  const currentUser = useSelector(getCurrentUser);

  const validHost = useRef((session as ZoomSession).authenticationEmail);
  const validAlternativeHosts = useRef((session as ZoomSession).alternativeHosts);
  const validCoOrganizers = useRef((session as MSTeamsSession).coorganizers);
  const hasInitialStartTime = useRef(!!session.startTime);
  // Track validity for computing allValid (both Zoom validators need each other's status)
  const isValidHost = useRef(!!validHost.current);
  // Empty alt hosts is valid (optional field); pre-existing hosts from the server are trusted as valid
  const isValidAlternativeHost = useRef(true);

  const name = `sessions.${session.index}`;
  const descriptionStyles = session.id ? css`border: 1px solid ${gray4};` : '';

  const { watch, formState, resetField } = useFormContext() || {};
  const startTime = watch?.(`${name}.startTime`);
  const timeZone = watch?.(`${name}.timeZone`);

  const shouldDisableInLinkedLesson = !!isLinked && session.index === 0;

  const inputStyles = css`
    .bs4-form-control, .live-session-description {
      background-color: ${disabled || shouldDisableInLinkedLesson ? 'unset' : 'white'};
    }
    .react-datepicker-wrapper {
      input {
        pointer-events: ${disabled || isContentManagementCollection ? 'none' : 'unset'};
      }
    }
    .bs4-input-group {
      .bs4-input-group-append {
        background-color: transparent;
        .bs4-input-group-text {
          background-color: white;
        }
      }
    }
  `;

  useEffect(() => {
    const originalStartTime = getMomentDate(session.startTime, 'L HH:mm', currentUser.timeZone)?.toString();
    // Check if the new starttime is the same as the default
    if (originalStartTime === startTime) {
      resetField(`${name}.startTime`);
    } else {
      const newDisabled = startTime && session.id && !formState.isDirty
        ? moment().isAfter(moment(startTime).subtract(10, 'minutes'))
        : false;
      setDisabled(newDisabled);
    }

    // Alternative hosts should be editable for future meetings
    // Only disable if meeting has started (current time is after the meeting start time)
    const newDisabledAlternativeHosts = startTime
      ? moment().isAfter(moment(startTime))
      : false;
    setDisabledAlternativeHosts(newDisabledAlternativeHosts);
  }, [startTime, formState.isDirty]);


  const shouldDisableMainHostForLinkedConfiguredSession = isLinked && hasInitialStartTime.current;
  // Main Zoom host field should not be editable for:
  // - content management collections
  // - linked lesson-library sessions already configured with start time
  // - normal (non-linked) live events with existing session.id
  const isZoomMainHostDisabled = isContentManagementCollection
    || shouldDisableMainHostForLinkedConfiguredSession
    || (!isLinked && !!session.id);
  // Alternative hosts field is disabled when:
  // - the meeting has already started (checked via disabledAlternativeHosts), or
  // - the session belongs to a content management collection (isContentManagementCollection)
  // Alternative hosts can be edited for future meetings even if the session already exists
  const isZoomAltHostDisabled = disabledAlternativeHosts || isContentManagementCollection;

  const disabledTextInCollection = isContentManagementCollection
    ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()
    : '';

  return (
    <div css={getSanitizedStyles([styles, inputStyles])} className='p-4 mb-4'>
      <div className='d-flex align-items-center mb-4'>
        {showIndex && (
          <div className='bg-gray-2 text-align-center bold badge-index mr-2'>
            {session.index + 1}
          </div>
        )}
        <div className='w-100'>
          <FormNvClickToEdit
            className={`live-event-topic w-100 ${disabled ? 'disabled' : ''}`}
            name={`${name}.title`}
            type={InputType.SHORT_TEXT}
            showInputWhenEmpty
            text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ADD_TITLE()}
            replaceText
            editable={!disabled && !shouldDisableInLinkedLesson}
            textContainerClassName='text-large-body bold'
            dataQa={config.pendo.liveEvent.liveEventSession.title}
            withForm
            tooltipText={shouldDisableInLinkedLesson ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_LESSON_LIBRARY_TOOLTIP() : ''}
            tooltipPlacement='top'
          />
        </div>
      </div>
      <NvTooltip
        text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_LESSON_LIBRARY_TOOLTIP()}
        placement='top'
        enabled={shouldDisableInLinkedLesson}
      >
        <div className='w-100'>
          <NvFroala
            withForm
            className='mb-4 live-session-description'
            css={descriptionStyles}
            preset={FroalaViewMode.NORMAL}
            uploadType={UploadType.NONE}
            name={`${name}.description`}
            max-length={sessionType === SessionTypes.ZOOM ? 2000 : -1}
            ariaLabel={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ADD_DESCRIPTION()}
            placeholder={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ADD_DESCRIPTION()}
            isDisabled={disabled || shouldDisableInLinkedLesson}
            dataQa={config.pendo.liveEvent.liveEventSession.description}
          />
        </div>
      </NvTooltip>
      <div className='timing mb-4 d-flex w-100 justify-content-between align-items-center'>
        <div className='timing-start-time'>
          <label className={labelClass}>
            {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.START_TIME()}
          </label>
          <NvTooltip
            text={disabledTextInCollection}
            placement='top'
            enabled={isContentManagementCollection}
          >
            <div>
              <NvDatePicker
                required
                withForm
                placement='bottom'
                min={moment()}
                disabled={disabled || isContentManagementCollection}
                name={`${name}.startTime`}
                type={DatePickerType.DATETIME}
                placeholder={disabledTextInCollection ?? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.START_TIME()}
                className='w-100'
                dataQa={config.pendo.liveEvent.liveEventSession.date}
                fixed
              />
            </div>
          </NvTooltip>
        </div>
        <div className={`timing-duration${disabled || isContentManagementCollection ? ' disabled' : ''}`}>
          <label className={labelClass}>
            {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.DURATION()}
          </label>
          <NvTooltip
            text={disabledTextInCollection}
            placement='top'
            enabled={isContentManagementCollection}
          >
            <div>
              <NvFormDropdown
                name={`${name}.duration`}
                items={durations.map((duration) => ({
                  id: duration,
                  type: 'text',
                  text: getDurationText(duration),
                  dataQa: `${config.pendo.liveEvent.liveEventSession.duration}-${duration}`,
                }))}
                isSmallSize
                disabled={disabled || isContentManagementCollection}
                title={disabledTextInCollection}
              />
            </div>
          </NvTooltip>
        </div>
      </div>
      <div className={`timing-timezone ${disabled || isContentManagementCollection ? ' disabled' : ''} mb-4`}>
        <label className={labelClass}>
          {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.TIME_ZONE()}
        </label>
        <NvTooltip
          text={disabledTextInCollection}
          placement='top'
          enabled={isContentManagementCollection}
        >
          <div>
            <NvTimezoneSelector
              withForm
              defaultTimezone={timeZone?.id ?? currentUser.timeZone}
              name={`${name}.timeZone`}
              className='timezone-selector bg-white'
              dropdownWidth={420}
              disabled={disabled || isContentManagementCollection}
              dropdownMaxHeight={420}
            />
          </div>
        </NvTooltip>
      </div>
      {sessionType === SessionTypes.MS_TEAMS && (
        <MSTeamsCoOrganizerValidator
          formName={`${name}.coorganizers`}
          disabled={disabledAlternativeHosts || isContentManagementCollection}
          onValidation={(isValid, coorganizers) => {
            onHostValidation?.(isValid, { coorganizers }, isValid);
          }}
          host={validCoOrganizers.current}
          tooltip={disabledTextInCollection}
        />
      )}
      {sessionType === SessionTypes.ZOOM && (
        <React.Fragment>
          {showZoomHostValidator && (
            <ZoomHostValidator
              formName={`${name}.authenticationEmail`}
              onValidation={(isValid, zoomHost) => {
                isValidHost.current = isValid;
                onHostValidation?.(isValid, { zoomHost }, isValid && isValidAlternativeHost.current);
              }}
              disabled={isZoomMainHostDisabled}
              host={validHost.current}
              tooltip={disabledTextInCollection}
              showValidator
            />
          )}
          <ZoomHostValidator
            formName={`${name}.alternativeHosts`}
            onValidation={(isValid, alternativeHosts) => {
              isValidAlternativeHost.current = isValid;
              onHostValidation?.(isValid, { alternativeHosts }, isValidHost.current && isValid);
            }}
            disabled={isZoomAltHostDisabled}
            className='alternative-hosts'
            data-qa={config.pendo.liveEvent.liveEventSession.zoom.alternativeHost}
            isAlternativeHost
            host={validAlternativeHosts.current}
            tooltip={disabledTextInCollection}
            showValidator={showZoomHostValidator}
          />
        </React.Fragment>
      )}
      {sessionType === SessionTypes.EXTERNAL && (
        <div>
          <NvTooltip
            text={disabledTextInCollection}
            placement='top'
            enabled={isContentManagementCollection}
          >
            <div>
              <NvTextInput
                withForm
                required
                autoComplete='off'
                errorOnTouching={false}
                className='link-to-join'
                name={`${name}.joinUrl`}
                disabled={disabled || isContentManagementCollection}
                data-qa={config.pendo.liveEvent.liveEventSession.external.joinUrl}
                ariaLabel={disabledTextInCollection ?? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.LINK_TO_JOIN()}
                placeholder={disabledTextInCollection ?? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.LINK_TO_JOIN_PLACEHOLDER()}
                withLabel
                label={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.LINK_TO_JOIN()}
                labelAlwaysShown
                labelClassName={labelClass}
              />
            </div>
          </NvTooltip>
        </div>
      )}
      {canDelete && (
        <div className='d-flex mt-4 justify-content-end'>
          <NvTooltip
            text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.DELETE_TOOLTIP()}
            placement='top'
            enabled={shouldDisableInLinkedLesson}
          >
            <ClickableContainer
              className={`d-flex text-small label ${shouldDisableInLinkedLesson ? 'gray-4' : 'gray-2 cursor-pointer'}`}
              onClick={deleteSession}
              data-qa={config.pendo.liveEvent.liveEventSession.removeSession}
              disabled={shouldDisableInLinkedLesson}
            >
              {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.REMOVE_SESSION()}
              <NvIcon
                icon='trash'
                size='smallest'
                className={`${!shouldDisableInLinkedLesson ? 'cursor-pointer' : ''} ml-2`}
              />
            </ClickableContainer>
          </NvTooltip>
        </div>
      )}
    </div>
  );
};

export default LiveEventSession;
